<template>
  <AuthLayout :mode="mode">
    <Section
      heading="PowerCast"
      description="Use this page to enter equipment into our database for others to find when they PowerSearch AND to email this info to all dealers who have signed up to receive that manufacturer's listings. The optional Date Available/Needed field is meant to inform your fellow traders when the equipment you are selling will be available, or when you need it. The Researching Box is to let them know if you are not yet a buyer/seller but are just researching the market."
      :mode="mode"
    />
    <AppCard :mode="mode" title="PowerCast Listing Form">
      <template #body>
        <Notes :list="notesList" />
        <div class="mt-6">
          <div class="grid grid-cols-2 gap-x-3 gap-y-4 sm:grid-cols-1 sm:gap-4">
            <AppSelect
              :mode="mode"
              title="Manufacturer"
              v-model="powersearch.mfr_id"
            >
              <template #options>
                <option
                  v-for="(mfr, key) in mfrs"
                  :key="key"
                  :value="mfr.mfr.mfr_id"
                >
                  {{ mfr.mfr.company }}
                </option>
              </template>
            </AppSelect>
            <AppSelect
              :mode="mode"
              title="Buy/Sell"
              v-model="powersearch.buy_sell"
            >
              <template #options>
                <option selected value="B">Buy</option>
                <option value="S">Sell</option>
              </template>
            </AppSelect>
            <AppSelect
              :mode="mode"
              title="Country"
              v-model="powersearch.location"
            >
              <template #options>
                <option value="">Country</option>
                <option
                  v-for="(country, key) in countries"
                  :value="country.code"
                  :key="key"
                >
                  {{ country.country }}
                </option>
              </template>
            </AppSelect>
            <AppInput
              :mode="mode"
              type="date"
              id="date"
              title="Date available/needed by"
              v-model="powersearch.need_by"
            />
            <div class="col-span-2 sm:col-span-1">
              <AppLabel :mode="mode" htmlFor="subject" title="Subject" />
              <div class="flex">
                <AppInput
                  :mode="mode"
                  v-model="powersearch.subject"
                  placeholder="Subject"
                />
              </div>
            </div>
            <div class="col-span-2 sm:col-span-1">
              <AppLabel :mode="mode" htmlFor="message" title="Message" />
              <div :class="[mode === 'dark' ? 'dark-editor' : '']">
                <v-md-editor
                  v-model="powersearch.details"
                  right-toolbar="preview sync-scroll fullscreen"
                  height="400px"
                ></v-md-editor>
              </div>
            </div>
            <div class="col-span-2 sm:col-span-1">
              <AppCheckbox
                :mode="mode"
                id="researching"
                title="Researching (Not ready to buy/sell)"
                v-model="powersearch.researching"
              />
            </div>
          </div>
          <div class="mt-6">
            <div class="grid grid-cols-2 gap-3 sm:grid-cols-1 sm:gap-4">
              <AppButton
                title="Powercast (Send to All)"
                @action="addPowerCast"
              />
              <AppButton
                title="Selective send"
                @action="selectiveSend"
                type="secondary"
              />
            </div>
          </div>
        </div>
      </template>
    </AppCard>
  </AuthLayout>
</template>

<script>
import Section from "@C/Common/Section.vue";
import ApiRequest from "@L/ApiRequest";
import { useGlobalErrorHandler } from "@L/errorHandler";
import { useUserStore } from "@S/user";
import { useToastStore } from "@S/toast";
import { useLoaderStore } from "@S/loader";
import { useCountriesStore } from "@S/countries";
import Notes from "@C/Common/Notes.vue";
import AppLabel from "@C/Form/AppLabel.vue";
import AppSelect from "@C/Form/AppSelect.vue";
import AppCard from "@C/Common/AppCard.vue";
import AppInput from "@C/Form/AppInput.vue";
import AuthLayout from "@A/AuthLayout.vue";
import AppCheckbox from "@C/Form/AppCheckbox.vue";
import AppButton from "@C/Common/AppButton.vue";

export default {
  name: "BroadcastEmail",
  components: {
    AuthLayout,
    Section,
    Notes,
    AppLabel,
    AppSelect,
    AppCard,
    AppInput,
    AppCheckbox,
    AppButton,
  },
  setup() {
    const userStore = useUserStore();
    const toastStore = useToastStore();
    const loaderStore = useLoaderStore();
    const countriesStore = useCountriesStore();
    const { handleError } = useGlobalErrorHandler();
    return {
      userStore,
      toastStore,
      loaderStore,
      countriesStore,
      handleError,
    };
  },
  props: {
    mode: String,
  },
  data() {
    return {
      mfrs: [],
      powersearch: {
        details: "",
        send_daily_summary: true,
        mfr_id: "",
        buy_sell: "B",
        need_by: new Date().toISOString().split('T')[0], // Set to today's date
        researching: false,
        location: "",
      },
      mailqueue: {
        recipient_list: "",
      },
      notesList: [
        "Clicking POWERCAST (SEND TO ALL) will send an instant e-mail to all who have selected to receive this manufacturer.",
        "Clicking Selective Send will allow you to see and select only the companies that will receive your instant e-mail.",
      ],
    };
  },
  mounted() {
    this.getMfrs();
    // Ensure countries are loaded
    if (this.countriesStore.countries.length === 0) {
      this.getCountries();
    }
  },
  methods: {
    async getCountries() {
      try {
        const { data } = await ApiRequest().get(`/countries`);
        if (data) {
          this.countriesStore.setCountries(data);
        }
      } catch (e) {
        this.handleError(e);
      }
    },
    async getMfrs() {
      this.loaderStore.setIsLoading(true);
      try {
        const { data } = await ApiRequest().get(`/mfr_summary`);
        if (data) {
          this.mfrs = data
            .filter((item) => item.mfr.company && item.mfr.company.trim() !== "");
            //.sort((a, b) => a.mfr.mfr.localeCompare(b.mfr.mfr));
          this.powersearch.mfr_id =
            this.mfrs && this.mfrs.length > 0 ? this.mfrs[0].mfr.mfr_id : "";
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    async addPowerCast() {
      this.loaderStore.setIsLoading(true);
      const payload = {
        powersearch: this.powersearch,
        mailqueue: {
          recipient_list:
            "ALL" + this.powersearch.buy_sell + this.powersearch.mfr_id,
        },
      };
      try {
        const { data } = await ApiRequest().post(`/powercast`, payload);
        if (data) {
          this.toastStore.show(
            "Thank you, your message has been sent",
            "success",
          );
          this.clearInputs();
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    selectiveSend() {
      try {
        if (!this.powersearch.need_by) {
          this.toastStore.show(
            "Date available/needed by field is required",
            "error",
          );
          return;
        }
        const data = this.powersearch;
        data.mfr = this.mfrs.find(
          (mfr) => mfr.mfr.mfr_id == this.powersearch.mfr_id,
        ).mfr.mfr;
        localStorage.setItem("selective-send-powercast", JSON.stringify(data));
        this.$router.push({
          name: "selective-broadcast-email",
          query: { type: "powercast" },
        });
      } catch (e) {
        this.handleError(e);
      }
    },
    clearInputs() {
      this.powersearch = {
        details: "",
        send_daily_summary: true,
        mfr_id: "",
        buy_sell: "B",
        need_by: new Date().toISOString().split('T')[0], // Set to today's date
        researching: false,
        location: "",
      };
      this.powersearch.mfr_id =
        this.mfrs && this.mfrs.length > 0 ? this.mfrs[0].mfr.mfr_id : "";
    },
  },
  computed: {
    countries() {
      return this.countriesStore.countries;
    },
  },
};
</script>
